import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  Person,
  Pets,
  Male,
  Female,
  Phone,
  VideoCameraFront,
} from "@mui/icons-material";
import dayjs from "dayjs";
import VideoConferencia from "@/components/videoConferencia/VideoConferencia";
import ChatMedPaciente from "@/components/videoConferencia/ChatMedPaciente";

// =============================================================================
// TIPOS - Estrutura FHIR dos dados
// =============================================================================

interface FHIRPatientPet {
  resourceType: string;
  id: string;
  extension?: Array<{
    url: string;
    extension?: Array<{
      url: string;
      valueCodeableConcept?: {
        coding: Array<{ system: string; code: string; display: string }>;
      };
    }>;
  }>;
  identifier?: Array<{ system: string; value: string }>;
  name?: Array<{ use: string; text: string }>;
  gender?: string;
  birthDate?: string;
  photo?: Array<{ contentType: string; data: string }>;
}

interface FHIRPatientTutor {
  resourceType: string;
  id: string;
  identifier?: Array<{
    type?: { coding: Array<{ system: string; code: string }> };
    system: string;
    value: string;
  }>;
  name?: Array<{ use: string; text: string }>;
  telecom?: Array<{ system: string; value: string }>;
  gender?: string;
  birthDate?: string;
}

// =============================================================================
// INTERFACE DO COMPONENTE
// =============================================================================

interface AttendanceHeaderProps {
  pet: FHIRPatientPet;
  tutor: FHIRPatientTutor;
  appointment?: any; // Appointment FHIR resource para acessar virtualService
  /** `attendance-channel` da Location (ex.: chat). Vindo do VetAttendanceProvider com includeLocation. */
  locationAttendanceChannel?: string | null;
}

// =============================================================================
// FUNÇÕES AUXILIARES - Extração de dados FHIR
// =============================================================================

/**
 * Extrai informações do pet a partir da estrutura FHIR
 */
const extractPetInfo = (pet: FHIRPatientPet) => {
  const animalExtension = pet?.extension?.find(
    (ext) =>
      ext.url === "http://hl7.org/fhir/StructureDefinition/patient-animal"
  );

  const getExtensionValue = (url: string) =>
    animalExtension?.extension?.find((ext) => ext.url === url)
      ?.valueCodeableConcept?.coding?.[0]?.display || "-";

  return {
    name: pet?.name?.[0]?.text || "-",
    species: getExtensionValue("species"),
    breed: getExtensionValue("breed"),
    color: getExtensionValue("animalColor"),
    genderStatus: getExtensionValue("genderStatus"),
    gender: pet?.gender,
    birthDate: pet?.birthDate,
    photo: pet?.photo?.[0]?.data
      ? `data:${pet.photo[0].contentType};base64,${pet.photo[0].data}`
      : "",
    id: pet?.identifier?.[0]?.value || pet?.id,
  };
};

/**
 * Extrai informações do tutor a partir da estrutura FHIR
 */
const extractTutorInfo = (tutor: FHIRPatientTutor) => {
  // Busca nome social (usual) ou oficial
  const socialName = tutor.name?.find((n) => n.use === "usual")?.text;
  const officialName = tutor.name?.find((n) => n.use === "official")?.text;

  // Busca telefone
  const phone = tutor.telecom?.find((t) => t.system === "phone")?.value || "-";

  // Busca CPF
  const cpf =
    tutor.identifier?.find((i) => i.type?.coding?.[0]?.code === "TAX")?.value ||
    "-";

  return {
    name: socialName || officialName || "-",
    cpf: formatCPF(cpf),
    phone: formatPhone(phone),
  };
};

/**
 * Formata CPF para exibição (XXX.XXX.XXX-XX)
 */
const formatCPF = (cpf: string): string => {
  const numbers = cpf.replace(/\D/g, "");
  if (numbers.length !== 11) return cpf;
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

/**
 * Formata telefone para exibição
 */
const formatPhone = (phone: string): string => {
  const numbers = phone.replace(/\D/g, "");
  if (numbers.length === 13) {
    // +55 61 99999-9999
    return numbers.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, "+$1 ($2) $3-$4");
  }
  if (numbers.length === 11) {
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  return phone;
};

/**
 * Calcula idade em formato compacto
 */
const calculateAge = (birthDate?: string): string => {
  if (!birthDate) return "-";
  const birth = dayjs(birthDate);
  const now = dayjs();
  const years = now.diff(birth, "year");
  const months = now.diff(birth, "month") % 12;

  if (years === 0) return `${months}m`;
  if (months === 0) return `${years}a`;
  return `${years}a ${months}m`;
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

// Componente wrapper que inicia automaticamente a videoconferência
const AutoStartVideoConferencia: React.FC<{
  roomUrl: string;
  appointmentData: {
    id: string;
    patientName: string;
    practitionerName: string;
    startTime: string;
  };
  onClose?: () => void;
}> = ({ roomUrl, appointmentData, onClose }) => {
  const [hasStarted, setHasStarted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasStarted) return;

    // Aguarda um delay para garantir que o VideoConferencia foi renderizado
    const timer = setTimeout(() => {
      // Procura o botão do VideoConferencia apenas dentro do container deste componente
      if (!containerRef.current) return;

      const allButtons = containerRef.current.querySelectorAll("button");

      for (const button of allButtons) {
        const svg = button.querySelector("svg");
        if (svg) {
          const hasVideoIcon =
            svg.getAttribute("data-testid") === "VideoCameraFrontIcon";

          const buttonText = (button.textContent || "").toLowerCase();
          const hasVideoText =
            buttonText.includes("tele") ||
            buttonText.includes("acessar") ||
            buttonText.includes("iniciar");

          if (hasVideoIcon || hasVideoText) {
            const event = new MouseEvent("click", {
              bubbles: true,
              cancelable: true,
              view: window,
            });
            button.dispatchEvent(event);
            (button as HTMLButtonElement).click();
            setHasStarted(true);
            break;
          }
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [hasStarted]);

  // Observa quando a janela da videoconferência é fechada
  useEffect(() => {
    if (!hasStarted || !onClose) return;

    let interval: NodeJS.Timeout | null = null;
    let observer: MutationObserver | null = null;

    const startCheckTimer = setTimeout(() => {
      let wasWindowOpen = false;
      let checkCount = 0;

      const checkVideoWindow = () => {
        checkCount++;

        const videoWindows = document.querySelectorAll(
          '[style*="z-index: 99999"], [style*="zIndex: 99999"]'
        );
        const hasWherebyEmbed = document.querySelector("whereby-embed");
        const isWindowOpen = videoWindows.length > 0 || !!hasWherebyEmbed;

        if (checkCount === 1) {
          wasWindowOpen = isWindowOpen;
          return;
        }

        if (wasWindowOpen && !isWindowOpen) {
          onClose();
          return;
        }

        wasWindowOpen = isWindowOpen;
      };

      interval = setInterval(checkVideoWindow, 500);
      observer = new MutationObserver(() => {
        checkVideoWindow();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }, 1500);

    return () => {
      clearTimeout(startCheckTimer);
      if (interval) {
        clearInterval(interval);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [hasStarted, onClose]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "absolute",
        top: "-9999px",
        left: "-9999px",
        width: "1px",
        height: "1px",
        overflow: "hidden",
      }}
    >
      <VideoConferencia roomUrl={roomUrl} appointmentData={appointmentData} />
    </Box>
  );
};

/**
 * Componente de cabeçalho do atendimento veterinário
 * Exibe informações do pet e do tutor de forma compacta
 *
 * @param pet - Dados FHIR do pet (Patient com extension patient-animal)
 * @param tutor - Dados FHIR do tutor (Patient)
 * @param appointment - Dados FHIR do appointment (opcional, para teleconsulta)
 */
export const AttendanceHeader: React.FC<AttendanceHeaderProps> = ({
  pet,
  tutor,
  appointment,
  locationAttendanceChannel = null,
}) => {
  // Extrai dados formatados do pet e tutor
  const petInfo = extractPetInfo(pet);
  const tutorInfo = extractTutorInfo(tutor);

  // Estado para controlar a videoconferência
  const [videoConferenceOpen, setVideoConferenceOpen] = useState(false);
  const [selectedVideoAppointment, setSelectedVideoAppointment] =
    useState<any>(null);

  // Extrai o virtualService do appointment se disponível
  const virtualServiceUrl =
    appointment?.virtualService?.[0]?.addressUrl || null;

  // Agendamentos a domicílio: não exibimos chat nem vídeo aqui (atendimento presencial itinerante).
  const isAppointmentDomicilio = !!(
    appointment?.extension?.find((ext: any) =>
      ext.url?.includes("home-visit-address"),
    )
  );

  /** Tele / chat virtuais podem ter sala Whereby no appointment; só exibimos vídeo quando NÃO é canal chat nem domicilio. */
  const isChatChannel = locationAttendanceChannel === "chat";

  // CPF do tutor (apenas dígitos) — sala P2P do ChatMedPaciente usa o mesmo id no lado paciente
  const tutorCpfRaw =
    tutor?.identifier
      ?.find((i) => i.type?.coding?.[0]?.code === "TAX")
      ?.value?.replace(/\D/g, "") || "";

  const showChatButton =
    !isAppointmentDomicilio &&
    isChatChannel &&
    !!tutorCpfRaw.trim();

  const showTeleButton =
    !isAppointmentDomicilio &&
    !isChatChannel &&
    !!virtualServiceUrl;

  // Função para abrir a videoconferência
  const handleOpenVideoConference = () => {
    if (!virtualServiceUrl || !appointment) return;

    // Extrai o nome do profissional do appointment
    const practitionerParticipant = appointment.participant?.find((p: any) =>
      p.actor?.reference?.includes("Practitioner")
    );
    const practitionerName = practitionerParticipant?.display || "Veterinário";

    const appointmentData = {
      id: appointment.id || "",
      patientName: petInfo.name,
      practitionerName: practitionerName,
      startTime: appointment.start || new Date().toISOString(),
    };

    setSelectedVideoAppointment({
      virtualService: virtualServiceUrl,
      appointmentData,
    });
    setVideoConferenceOpen(true);
  };

  return (
    <Paper sx={{ p: 1.5, mb: 2 }}>
      {/* Cabeçalho compacto em linha única */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        divider={<Divider orientation="vertical" flexItem />}
      >
        {/* Seção do Pet */}
        <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
          {/* Avatar do Pet */}
          <Avatar
            src={petInfo.photo || undefined}
            sx={{
              width: 48,
              height: 48,
              border: "2px solid",
              borderColor: petInfo.gender === "male" ? "#1976d2" : "#d81b60",
              bgcolor:
                petInfo.gender === "male"
                  ? "rgba(25, 118, 210, 0.1)"
                  : "rgba(216, 27, 96, 0.1)",
            }}
          >
            <Pets
              sx={{
                fontSize: 24,
                color: petInfo.gender === "male" ? "#1976d2" : "#d81b60",
              }}
            />
          </Avatar>

          {/* Informações do Pet */}
          <Box flex={1}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                {petInfo.name}
              </Typography>
              {petInfo.gender === "male" ? (
                <Male fontSize="small" sx={{ color: "#1976d2" }} />
              ) : (
                <Female fontSize="small" sx={{ color: "#d81b60" }} />
              )}
              <Chip
                label={petInfo.id}
                size="small"
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: "rgba(0, 168, 132, 0.1)",
                  color: "#00A884",
                  fontFamily: "monospace",
                }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {petInfo.species} • {petInfo.breed} • {petInfo.color} •{" "}
              {calculateAge(petInfo.birthDate)} • {petInfo.genderStatus}
            </Typography>
          </Box>
        </Stack>

        {/* Seção do Tutor */}
        <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
          {/* Avatar do Tutor */}
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: "#00A884",
            }}
          >
            <Person sx={{ fontSize: 24 }} />
          </Avatar>

          {/* Informações do Tutor */}
          <Box flex={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                {tutorInfo.name}
              </Typography>
              <Chip
                label={`CPF: ${tutorInfo.cpf}`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Phone sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                {tutorInfo.phone}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        {/* Chat só no canal chat; vídeo só em tele/virtual (não-chat); domicílio não exibe botão aqui */}
        {showChatButton ? (
          <Box>
            {/* ChatMedPaciente (role doctor) já rotula “Iniciar Chat” */}
            <ChatMedPaciente
              role="doctor"
              patientCpf={tutorCpfRaw}
              cardMode={false}
            />
          </Box>
        ) : showTeleButton ? (
          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VideoCameraFront />}
              onClick={handleOpenVideoConference}
              sx={{
                borderColor: "#00A884",
                color: "#00A884",
                "&:hover": {
                  borderColor: "#008c6e",
                  bgcolor: "rgba(0, 168, 132, 0.04)",
                },
              }}
            >
              Iniciar Teleatendimento
            </Button>
          </Box>
        ) : null}
      </Stack>

      {/* VideoConferencia para teleatendimento - inicia automaticamente quando renderizado */}
      {videoConferenceOpen && selectedVideoAppointment?.virtualService && (
        <AutoStartVideoConferencia
          roomUrl={selectedVideoAppointment.virtualService}
          appointmentData={selectedVideoAppointment.appointmentData}
          onClose={() => {
            setVideoConferenceOpen(false);
            setSelectedVideoAppointment(null);
          }}
        />
      )}
    </Paper>
  );
};

export default AttendanceHeader;
